import { Card, CardHeader, DescriptionItem, DescriptionList } from "@/components/ui/card";
import { Callout } from "@/components/ui/feedback";
import { contactName } from "@/lib/contacts";
import { emergencyCallWho, emergencyContactOf, emergencyField } from "@/lib/emergency-contact";
import { isPstnNumber, sameNumber } from "@/lib/phone-number";
import { useContactFields } from "@/lib/queries";
import { RECOVERY_STAGE } from "@/lib/stages";
import type { LeadCard } from "@/lib/types";
import { formatPhoneNumber } from "@/lib/utils";
import { DialNumberButton } from "@/softphone/dial-number";

/**
 * The lead's emergency contact on their record (ACL-382): who they are, how they are related, the
 * number to call them on, and whose case the call is for. Read from the contact form's own answer
 * (`lib/emergency-contact.ts`), from the same cache entry the field cards read, so it costs no
 * request of its own.
 *
 * Drawn only where the answer has something, and for an MIA lead always: that is the client the
 * firm cannot reach, and the rep must see at once whether there is somebody else to ask. A role the
 * server hides the field from gets nothing, MIA or not.
 *
 * The number is called through the same confirmation as any number on the record, which names the
 * lead the call is for. It is placed for the lead, so it is saved on their record (core-api checks
 * the number is in their answer), and only through a campaign.
 */
export function EmergencyContactCard({ contact }: { contact: LeadCard }) {
  const fields = useContactFields(contact.id);
  const found = fields.data ? emergencyField(fields.data.sections) : null;
  if (!found) return null;
  const emergency = emergencyContactOf(found.field);
  const mia = contact.stage === RECOVERY_STAGE;
  if (!emergency && !mia) return null;

  const lead = contactName(contact);
  const forWhom = lead ?? "this contact";
  const notGiven = <span className="text-text-muted">Not given</span>;
  const ownNumber = emergency?.phone ? sameNumber(emergency.phone, contact.phone) : false;

  return (
    <Card>
      <CardHeader title="Emergency contact" />
      {mia && (
        <div className="px-4 pt-3">
          <Callout tone="warning">
            {lead ?? "This contact"} is MIA: the firm cannot reach them. Their emergency contact may know how to.
          </Callout>
        </div>
      )}
      {emergency ? (
        <DescriptionList className="px-4">
          <DescriptionItem label="Name">{emergency.name ?? notGiven}</DescriptionItem>
          <DescriptionItem label="Relationship">{emergency.relationship ?? notGiven}</DescriptionItem>
          <DescriptionItem label="Phone">
            {!emergency.phone ? (
              <span className="text-text-muted">No number given</span>
            ) : ownNumber ? (
              <>
                <span className="font-mono">{formatPhoneNumber(emergency.phone)}</span>
                <span className="block text-xs text-text-muted">The same number as {forWhom}'s own phone</span>
              </>
            ) : !isPstnNumber(emergency.phone) ? (
              // A call for the lead goes through a campaign, which dials only these.
              <>
                <span className="font-mono">{emergency.phone}</span>
                <span className="block text-xs text-text-muted">Not a US or Canadian number, so it cannot be called from here</span>
              </>
            ) : (
              <span className="font-mono">
                <DialNumberButton
                  number={emergency.phone}
                  name={emergencyCallWho(emergency, lead)}
                  forLead={{ id: contact.id, name: lead }}
                />
              </span>
            )}
            {emergency.moreNumbers > 0 && (
              <span className="block text-xs text-text-muted">
                {emergency.moreNumbers === 1 ? "One more number" : `${emergency.moreNumbers} more numbers`} in
                the answer under {found.section.title}
              </span>
            )}
          </DescriptionItem>
          {emergency.note && <DescriptionItem label="Note">{emergency.note}</DescriptionItem>}
          <DescriptionItem label="For">{forWhom}</DescriptionItem>
        </DescriptionList>
      ) : (
        <p className="px-4 py-3 text-sm text-text-muted">
          No emergency contact on file. Add one under {found.section.title}.
        </p>
      )}
    </Card>
  );
}
